/**
 * Persistence + sharing for free-tool inputs.
 *
 * Most tools keep their work only in React state, so refreshing (or signing up)
 * lost the user's result. This module snapshots the form controls rendered by a
 * tool and can restore them later. It intentionally works at the DOM level so
 * all 100+ tools benefit without rewriting every component.
 *
 * For richer tools, a component can opt out of a field with `data-no-persist`.
 */

export const TOOL_DRAFT_VERSION = 1;
export const TOOL_DRAFT_PREFIX = 'orgsets_tool_draft_v1:';
export const PENDING_TOOL_DRAFT_KEY = 'pending_tool_org_draft';

export type ToolFieldTag = 'input' | 'select' | 'textarea';

export interface ToolFieldSnapshot {
  /** Position of the control in the tool DOM. Used as a fallback when matching. */
  index: number;
  tag: ToolFieldTag;
  type: string;
  /** name, id, aria-label, placeholder, or a generated fallback. */
  key: string;
  value: string;
  checked?: boolean;
  values?: string[];
}

export interface ToolDraft {
  version: number;
  slug: string;
  savedAt: string;
  fields: ToolFieldSnapshot[];
}

export interface PendingToolDraft {
  toolSlug: string;
  toolTitle: string;
  orgName: string;
  timestamp: string;
  stateKey: string;
  hasState: boolean;
  source?: string;
}

const SKIPPED_INPUT_TYPES = new Set([
  'button',
  'submit',
  'reset',
  'file',
  'password',
  'hidden',
  'image',
]);

const MAX_FIELD_VALUE_LENGTH = 20_000;
const MAX_SHARE_URL_LENGTH = 7_500;

function getControlKey(el: HTMLElement, index: number): string {
  return (
    el.getAttribute('name') ||
    el.id ||
    el.getAttribute('aria-label') ||
    el.getAttribute('placeholder') ||
    `${el.tagName.toLowerCase()}-${index}`
  );
}

function shouldSkipControl(el: HTMLElement): boolean {
  if (el.hasAttribute('data-no-persist')) return true;
  if (el.getAttribute('aria-hidden') === 'true') return true;
  if ((el as HTMLInputElement).disabled) return true;

  if (el instanceof HTMLInputElement) {
    if (SKIPPED_INPUT_TYPES.has(el.type)) return true;
  }

  return false;
}

export function captureToolFields(root: HTMLElement): ToolFieldSnapshot[] {
  const controls = Array.from(
    root.querySelectorAll<HTMLElement>('input, select, textarea'),
  );

  return controls.reduce<ToolFieldSnapshot[]>((fields, el, index) => {
    if (shouldSkipControl(el)) return fields;

    const tag = el.tagName.toLowerCase() as ToolFieldTag;
    const key = getControlKey(el, index);
    const snapshot: ToolFieldSnapshot = {
      index,
      tag,
      type: (el as HTMLInputElement).type || '',
      key,
      value: '',
    };

    if (el instanceof HTMLInputElement) {
      snapshot.value = el.value.slice(0, MAX_FIELD_VALUE_LENGTH);
      if (el.type === 'checkbox' || el.type === 'radio') {
        snapshot.checked = el.checked;
      }
    } else if (el instanceof HTMLTextAreaElement) {
      snapshot.value = el.value.slice(0, MAX_FIELD_VALUE_LENGTH);
    } else if (el instanceof HTMLSelectElement) {
      snapshot.values = Array.from(el.selectedOptions)
        .map(option => option.value)
        .slice(0, 50);
      snapshot.value = el.value;
    }

    fields.push(snapshot);
    return fields;
  }, []);
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const prototype =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
}

function setNativeChecked(el: HTMLInputElement, checked: boolean): void {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'checked')?.set;
  if (setter) {
    setter.call(el, checked);
  } else {
    el.checked = checked;
  }
}

function fireEvents(el: HTMLElement): void {
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

function matchesField(el: HTMLElement, field: ToolFieldSnapshot): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag !== field.tag) return false;

  if (el instanceof HTMLInputElement && field.type && el.type !== field.type) {
    return false;
  }

  return getControlKey(el, field.index) === field.key;
}

/**
 * Restores a snapshot and returns how many controls were updated. React's
 * controlled inputs listen for native events, so using the prototype setter is
 * important here.
 */
export function restoreToolFields(root: HTMLElement, fields: ToolFieldSnapshot[]): number {
  if (!fields.length) return 0;

  const controls = Array.from(
    root.querySelectorAll<HTMLElement>('input, select, textarea'),
  );

  let restored = 0;

  for (const field of fields) {
    let target =
      controls.find(el => matchesField(el, field)) ||
      controls[field.index];

    if (!target) continue;
    if (target.tagName.toLowerCase() !== field.tag) continue;

    try {
      if (target instanceof HTMLInputElement) {
        if (field.type === 'checkbox' || field.type === 'radio') {
          setNativeChecked(target, Boolean(field.checked));
        } else {
          setNativeValue(target, field.value);
        }
        fireEvents(target);
        restored += 1;
      } else if (target instanceof HTMLTextAreaElement) {
        setNativeValue(target, field.value);
        fireEvents(target);
        restored += 1;
      } else if (target instanceof HTMLSelectElement) {
        const desired = new Set(field.values?.length ? field.values : [field.value]);
        for (const option of Array.from(target.options)) {
          option.selected = desired.has(option.value);
        }
        if (field.value) {
          const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
          if (setter) {
            setter.call(target, field.value);
          } else {
            target.value = field.value;
          }
        }
        fireEvents(target);
        restored += 1;
      }
    } catch {
      // A single stubborn field should not prevent the rest of the draft.
    }
  }

  return restored;
}

export function draftStorageKey(slug: string): string {
  return `${TOOL_DRAFT_PREFIX}${slug}`;
}

export function saveToolDraft(draft: ToolDraft): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(draftStorageKey(draft.slug), JSON.stringify(draft));
  } catch {
    // Storage can be full or blocked (private mode). Drafting is best effort.
  }
}

export function loadToolDraft(slug: string): ToolDraft | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(draftStorageKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ToolDraft;
    if (!parsed || parsed.slug !== slug || !Array.isArray(parsed.fields)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearToolDraft(slug: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(draftStorageKey(slug));
  } catch {
    // Ignore.
  }
}

export function getPendingToolDraft(): PendingToolDraft | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PENDING_TOOL_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingToolDraft;
    if (!parsed?.toolSlug) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setPendingToolDraft(draft: PendingToolDraft): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PENDING_TOOL_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Ignore.
  }
}

export function clearPendingToolDraft(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(PENDING_TOOL_DRAFT_KEY);
  } catch {
    // Ignore.
  }
}

/** Creates or refreshes the dashboard "continue your draft" pointer. */
export function ensurePendingToolDraft(
  slug: string,
  toolTitle: string,
  orgName = '',
  source = 'tool',
): void {
  const existing = getPendingToolDraft();
  const draft = loadToolDraft(slug);
  setPendingToolDraft({
    toolSlug: slug,
    toolTitle,
    orgName: orgName || existing?.orgName || '',
    timestamp: new Date().toISOString(),
    stateKey: draftStorageKey(slug),
    hasState: Boolean(draft?.fields?.length),
    source,
  });
}

function toBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Encodes only the fields needed for a share link (no timestamps/version noise). */
export function encodeToolDraft(draft: ToolDraft): string | null {
  try {
    const encoded = toBase64(
      JSON.stringify({ s: draft.slug, f: draft.fields }),
    );
    if (encoded.length > MAX_SHARE_URL_LENGTH) return null;
    return encoded;
  } catch {
    return null;
  }
}

export function decodeToolDraft(encoded: string): ToolDraft | null {
  try {
    const parsed = JSON.parse(fromBase64(encoded)) as {
      s?: string;
      f?: ToolFieldSnapshot[];
    };
    if (!parsed?.s || !Array.isArray(parsed.f)) return null;
    return {
      version: TOOL_DRAFT_VERSION,
      slug: parsed.s,
      savedAt: new Date().toISOString(),
      fields: parsed.f,
    };
  } catch {
    return null;
  }
}
